import { useState } from "react";
import {
  Asset,
  ASSET_TYPE_LABELS,
  ASSET_TYPE_CONFIG,
} from "../../types/asset-types";
import { AssetLinkManagement } from "./asset-link-management";

export interface AssetDetailModalProps {
  asset: Asset | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (asset: Asset) => void;
}

export function AssetDetailModal({
  asset,
  isOpen,
  onClose,
  onEdit,
}: AssetDetailModalProps) {
  const [activeTab, setActiveTab] = useState<
    "details" | "files" | "conversations" | "links"
  >("links");

  if (!isOpen || !asset) return null;

  const config = ASSET_TYPE_CONFIG[asset.type];

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Render metadata based on asset type
  const renderMetadata = () => {
    if (!asset.metadata) return null;

    switch (asset.type) {
      case "STATIONARY":
        const stationaryMeta = asset.metadata as any;
        return (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Location
              </label>
              <p className="text-gray-900">
                {stationaryMeta.locationDescription}
              </p>
            </div>
            {stationaryMeta.latitude && stationaryMeta.longitude && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Coordinates
                </label>
                <p className="text-gray-900">
                  {stationaryMeta.latitude}, {stationaryMeta.longitude}
                </p>
              </div>
            )}
            {stationaryMeta.floor && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Floor/Level
                </label>
                <p className="text-gray-900">{stationaryMeta.floor}</p>
              </div>
            )}
          </div>
        );

      case "MOBILE":
        const mobileMeta = asset.metadata as any;
        return (
          <div className="space-y-3">
            {mobileMeta.vehicleId && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Vehicle ID
                </label>
                <p className="text-gray-900">{mobileMeta.vehicleId}</p>
              </div>
            )}
            {mobileMeta.route && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Route
                </label>
                <p className="text-gray-900">{mobileMeta.route}</p>
              </div>
            )}
            {mobileMeta.operator && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Operator
                </label>
                <p className="text-gray-900">{mobileMeta.operator}</p>
              </div>
            )}
          </div>
        );

      case "PRINT":
        const printMeta = asset.metadata as any;
        return (
          <div className="space-y-3">
            {printMeta.publication && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Publication
                </label>
                <p className="text-gray-900">{printMeta.publication}</p>
              </div>
            )}
            {printMeta.issueDate && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Issue Date
                </label>
                <p className="text-gray-900">
                  {formatDate(printMeta.issueDate)}
                </p>
              </div>
            )}
            {printMeta.pageNumber && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Page Number
                </label>
                <p className="text-gray-900">{printMeta.pageNumber}</p>
              </div>
            )}
            {printMeta.adSize && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Ad Size
                </label>
                <p className="text-gray-900">{printMeta.adSize}</p>
              </div>
            )}
          </div>
        );

      case "DIGITAL":
        const digitalMeta = asset.metadata as any;
        return (
          <div className="space-y-3">
            {digitalMeta.platform && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Platform
                </label>
                <p className="text-gray-900">{digitalMeta.platform}</p>
              </div>
            )}
            {digitalMeta.conversationId && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Conversation ID
                </label>
                <p className="text-gray-900">{digitalMeta.conversationId}</p>
              </div>
            )}
            {digitalMeta.website && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Website
                </label>
                <p className="text-gray-900">{digitalMeta.website}</p>
              </div>
            )}
          </div>
        );

      case "SEATING":
        const seatingMeta = asset.metadata as any;
        return (
          <div className="space-y-3">
            {seatingMeta.section && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Section
                </label>
                <p className="text-gray-900">{seatingMeta.section}</p>
              </div>
            )}
            {seatingMeta.level && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Level
                </label>
                <p className="text-gray-900">{seatingMeta.level}</p>
              </div>
            )}
            {seatingMeta.seatRange && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Seat Range
                </label>
                <p className="text-gray-900">{seatingMeta.seatRange}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="asset-detail-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="asset-detail-title"
    >
      <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div
              className={`w-12 h-12 ${config.bgColor} rounded-lg flex items-center justify-center`}
            >
              <span className="text-2xl">{config.icon}</span>
            </div>
            <div>
              <h2
                id="asset-detail-title"
                className="text-xl font-semibold text-gray-900"
              >
                {asset.name}
              </h2>
              <p className="text-sm text-gray-500">
                {ASSET_TYPE_LABELS[asset.type]}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onEdit && (
              <button
                onClick={() => onEdit(asset)}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                data-testid="edit-asset-button"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-modal-button"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" role="tablist">
            {/* <button
              onClick={() => setActiveTab("details")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "details"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              role="tab"
              aria-selected={activeTab === "details"}
              data-testid="details-tab"
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab("files")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "files"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              role="tab"
              aria-selected={activeTab === "files"}
              data-testid="files-tab"
            >
              Files ({asset.files.length})
            </button> */}
            <button
              onClick={() => setActiveTab("links")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "links"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              role="tab"
              aria-selected={activeTab === "links"}
              data-testid="links-tab"
            >
              Links & QR Codes
            </button>
            <button
              onClick={() => setActiveTab("conversations")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "conversations"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              role="tab"
              aria-selected={activeTab === "conversations"}
              data-testid="conversations-tab"
            >
              Conversations ({asset.conversationIds.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto max-h-[60vh]"
          role="tabpanel"
          data-testid={`${activeTab}-panel`}
        >
          {activeTab === "details" && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Name
                    </label>
                    <p className="text-gray-900">{asset.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Type
                    </label>
                    <p className="text-gray-900">
                      {ASSET_TYPE_LABELS[asset.type]}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        asset.status === "ACTIVE"
                          ? "bg-green-100 text-green-800"
                          : asset.status === "INACTIVE"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {asset.status.toLowerCase()}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Created
                    </label>
                    <p className="text-gray-900">
                      {formatDate(asset.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description - Note: Description field not in Asset type yet */}
              {/* {asset.description && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Description
                  </h3>
                  <p className="text-gray-700">{asset.description}</p>
                </div>
              )} */}

              {/* Venue Information */}
              {asset.venue && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Venue
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Name
                        </label>
                        <p className="text-gray-900">{asset.venue.name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Type
                        </label>
                        <p className="text-gray-900">{asset.venue.type}</p>
                      </div>
                      {asset.venue.address && (
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-gray-500">
                            Address
                          </label>
                          <p className="text-gray-900">{asset.venue.address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Type-specific Metadata */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Asset Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {renderMetadata()}
                </div>
              </div>
            </div>
          )}

          {activeTab === "files" && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Associated Files
              </h3>
              {asset.files.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500">
                    No files attached to this asset
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {asset.files.map((file) => (
                    <div
                      key={file.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 truncate">
                            {file.filename}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(file.size)} • {file.contentType}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-gray-400">
                          Uploaded {formatDate(file.uploadedAt)}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              window.open(
                                `/api/files/preview/${file.id}`,
                                "_blank"
                              )
                            }
                            className="text-green-600 hover:text-green-700 text-sm font-medium"
                          >
                            Preview
                          </button>
                          <a
                            href={`/api/files/download/${file.id}`}
                            download={file.filename}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "conversations" && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Conversation Associations
              </h3>
              {asset.conversationIds.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500">
                    This asset is not attached to any conversations
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {asset.conversationIds.map((conversationId) => (
                    <div
                      key={conversationId}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            Conversation {conversationId}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Active conversation
                          </p>
                        </div>
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                          View Conversation
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "links" && (
            <div className="space-y-4">
              <AssetLinkManagement asset={asset} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
